import React from 'react';
import { FiInbox, FiClock, FiHelpCircle, FiTruck, FiMapPin } from 'react-icons/fi';

// ═══════════════════════════════════════════════════════════════
// Shared PST date helpers + ops analytics UI.
// Used by DispatchScreen and CommandCenterScreen so the two stay in sync.
// ═══════════════════════════════════════════════════════════════

export function getPSTDateString(date = new Date()) {
  const pstDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const year = pstDate.getFullYear();
  const month = String(pstDate.getMonth() + 1).padStart(2, '0');
  const day = String(pstDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getOrderDateInPST(inStockDate) {
  if (!inStockDate) return null;
  return getPSTDateString(new Date(inStockDate));
}

export function isTerminalOrderStatus(status) {
  return status === 'completed' || status === 'canceled' || status === 'cancelled';
}

// Is the order's in-stock date within the next `days` days (PST), counting
// today? Orders without an in-stock date are left in to avoid hiding
// incomplete data.
export function isOrderWithinNextDaysPST(order, days) {
  if (!Number.isFinite(days) || days <= 0) return true;
  const dateStr = getOrderDateInPST(order?.inStockDate);
  if (!dateStr) return true;
  const todayStr = getPSTDateString();
  if (dateStr < todayStr) return false;
  const limit = new Date();
  limit.setDate(limit.getDate() + (days - 1));
  const limitStr = getPSTDateString(limit);
  return dateStr <= limitStr;
}

export const OPS_WINDOW_OPTIONS = [
  { value: 1, label: 'Today' },
  { value: 3, label: 'Next 3d' },
  { value: 7, label: 'Next 7d' },
  { value: 14, label: 'Next 14d' },
  { value: null, label: 'All' },
];

// Turn a window value (1/3/7/14/null) into a URL-safe slug for deep-links.
export function opsWindowToParam(days) {
  if (days === null || days === undefined) return 'all';
  return String(days);
}

export function opsWindowFromParam(param) {
  if (!param) return undefined;
  if (param === 'all') return null;
  const n = Number(param);
  if (!Number.isFinite(n)) return undefined;
  return n;
}

export function OpsWindowSelector({ value, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-xl bg-gray-100 dark:bg-gray-800 p-1">
      {OPS_WINDOW_OPTIONS.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
              isActive
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function OpsStatCard({ label, count, sub, tone = 'gray', active = false, onClick, icon: Icon, pulse = false }) {
  const toneMap = {
    gray: {
      base: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
      count: 'text-gray-900 dark:text-white',
      icon: 'text-gray-400',
      ring: active ? 'ring-2 ring-gray-900 dark:ring-white' : '',
    },
    indigo: {
      base: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800/60',
      count: 'text-indigo-700 dark:text-indigo-300',
      icon: 'text-indigo-500',
      ring: active ? 'ring-2 ring-indigo-500' : '',
    },
    amber: {
      base: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/60',
      count: 'text-amber-800 dark:text-amber-300',
      icon: 'text-amber-500',
      ring: active ? 'ring-2 ring-amber-500' : '',
    },
    coral: {
      base: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/60',
      count: 'text-red-700 dark:text-red-300',
      icon: 'text-red-500',
      ring: active ? 'ring-2 ring-red-500' : '',
    },
    violet: {
      base: 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800/60',
      count: 'text-violet-700 dark:text-violet-300',
      icon: 'text-violet-500',
      ring: active ? 'ring-2 ring-violet-500' : '',
    },
    rose: {
      base: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800/60',
      count: 'text-rose-700 dark:text-rose-300',
      icon: 'text-rose-500',
      ring: active ? 'ring-2 ring-rose-500' : '',
    },
    emerald: {
      base: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/60',
      count: 'text-emerald-700 dark:text-emerald-300',
      icon: 'text-emerald-500',
      ring: active ? 'ring-2 ring-emerald-500' : '',
    },
    sky: {
      base: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800/60',
      count: 'text-sky-700 dark:text-sky-300',
      icon: 'text-sky-500',
      ring: active ? 'ring-2 ring-sky-500' : '',
    },
  };
  const t = toneMap[tone] || toneMap.gray;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex min-w-[170px] flex-1 items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all hover:shadow-md ${t.base} ${t.ring}`}
    >
      {Icon ? (
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/70 dark:bg-white/10 ${t.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <p className={`text-2xl font-bold leading-none ${t.count}`}>{count}</p>
        {sub ? <p className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400">{sub}</p> : null}
      </div>
      {pulse && count > 0 ? (
        <span className="absolute right-3 top-3 inline-flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
        </span>
      ) : null}
    </button>
  );
}

export function DispatchOpsStats({
  orders,
  quickFilter,
  setQuickFilter,
  inquiriesCount,
  onOpenInquiries,
  driverSparseCount = 0,
  driverSparseSub = 'Active orders with <3 drivers in range',
  windowDays,
  onChangeWindowDays,
  showWindowSelector = true,
  title = 'Operations overview',
}) {
  const activeOrdersInWindow = orders.filter(o => (
    !isTerminalOrderStatus(o.status) && isOrderWithinNextDaysPST(o, windowDays)
  ));

  const deliveryTimeMissing = activeOrdersInWindow.filter(o => {
    if (o.deliveryFeeType === 'deliver_to_door') return false;
    if (o.needsDeliveryTime === true) return true;
    return !o.storeArrivalTime;
  }).length;

  const unplaced = activeOrdersInWindow.filter(o => {
    if (o.storeOrderId || (o.storeOrderConfirmationUrl && o.storeOrderConfirmationUrl.trim())) return false;
    if (o.storeSpend && Number(o.storeSpend) > 0) return false;
    return true;
  }).length;

  const noDriver = activeOrdersInWindow.filter(o => !o.driverID).length;

  const cycle = (key) => setQuickFilter(quickFilter === key ? null : key);

  const windowLabel = OPS_WINDOW_OPTIONS.find(o => o.value === windowDays)?.label || 'All';
  const scopeSuffix = windowDays ? ` · ${windowLabel.toLowerCase()}` : '';

  return (
    <div className="space-y-3">
      {(title || showWindowSelector) ? (
        <div className="flex items-center justify-between flex-wrap gap-2">
          {title ? (
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {title}
              {windowDays ? (
                <span className="ml-2 normal-case tracking-normal text-gray-400 dark:text-gray-500">
                  (orders scoped to {windowLabel.toLowerCase()})
                </span>
              ) : null}
            </div>
          ) : <span />}
          {showWindowSelector ? (
            <OpsWindowSelector value={windowDays} onChange={onChangeWindowDays} />
          ) : null}
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <OpsStatCard
          label="Inquiries"
          count={inquiriesCount.open}
          sub={`${inquiriesCount.today} today · ${inquiriesCount.awaiting} awaiting reply`}
          tone="indigo"
          icon={FiInbox}
          pulse={inquiriesCount.awaiting > 0}
          onClick={onOpenInquiries}
        />
        <OpsStatCard
          label="Delivery time missing"
          count={deliveryTimeMissing}
          sub={`No arrival window${scopeSuffix}`}
          tone="amber"
          icon={FiClock}
          active={quickFilter === 'delivery-time-missing'}
          onClick={() => cycle('delivery-time-missing')}
        />
        <OpsStatCard
          label="Unplaced orders"
          count={unplaced}
          sub={`No store order id yet${scopeSuffix}`}
          tone="coral"
          icon={FiHelpCircle}
          active={quickFilter === 'unplaced'}
          onClick={() => cycle('unplaced')}
        />
        <OpsStatCard
          label="No driver"
          count={noDriver}
          sub={`Waiting on assignment${scopeSuffix}`}
          tone="violet"
          icon={FiTruck}
          active={quickFilter === 'no-driver'}
          onClick={() => cycle('no-driver')}
        />
        <OpsStatCard
          label="Driver-sparse orders"
          count={driverSparseCount}
          sub={driverSparseSub}
          tone="rose"
          icon={FiMapPin}
          active={quickFilter === 'driver-sparse'}
          pulse={driverSparseCount > 0}
          onClick={() => cycle('driver-sparse')}
        />
      </div>
    </div>
  );
}
